import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getPaymentPackages, createSubscriptionCheckout, createCreditsCheckout, getCurrentUser } from '@/lib/api';
import { CreditCard, Zap, Check, Loader2, Crown } from 'lucide-react';

function PlanItem({ name, price, features, isCurrent, isPopular, disabled, onSelect }) {
  return (
    <Card className={isPopular ? 'border-blue-500 shadow-lg' : isCurrent ? 'border-green-500' : ''}>
      <CardHeader className="pb-2 text-center">
        {isPopular && <Badge className="bg-blue-600 text-white mb-2 mx-auto">Popular</Badge>}
        <CardTitle className="text-lg">{name}</CardTitle>
        <div className="mt-2">
          <span className="text-3xl font-bold">${price}</span>
          <span className="text-slate-500 text-sm">/mo</span>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <ul className="space-y-1 mb-4 text-sm">
          {features.slice(0, 3).map((f, i) => (
            <li key={i} className="flex gap-2">
              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="text-xs">{f}</span>
            </li>
          ))}
        </ul>
        <Button
          size="sm"
          className="w-full"
          variant={name === 'FREE' || isCurrent ? 'outline' : 'default'}
          disabled={name === 'FREE' || isCurrent || disabled}
          onClick={onSelect}
        >
          {isCurrent ? 'Current' : name === 'FREE' ? 'Free' : 'Upgrade'}
        </Button>
      </CardContent>
    </Card>
  );
}

function CreditsItem({ name, price, credits, isBest, disabled, onSelect }) {
  return (
    <Card className={isBest ? 'border-blue-500 shadow-lg' : ''}>
      <CardHeader className="text-center pb-2">
        {isBest && <Badge className="bg-blue-600 text-white mb-2 mx-auto">Best Value</Badge>}
        <Zap className="h-8 w-8 text-blue-600 mx-auto mb-2" />
        <CardTitle className="text-lg">{name}</CardTitle>
        <div className="mt-2">
          <span className="text-3xl font-bold">${price}</span>
        </div>
        <p className="text-xs text-slate-500">${(price / credits).toFixed(2)}/analysis</p>
      </CardHeader>
      <CardContent className="pt-2">
        <Button className="w-full" onClick={onSelect} disabled={disabled}>
          Purchase
        </Button>
      </CardContent>
    </Card>
  );
}

export function BillingModal({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [tab, setTab] = useState('plans');
  
  // Store data as simple values
  const [freePlan, setFreePlan] = useState(null);
  const [starterPlan, setStarterPlan] = useState(null);
  const [proPlan, setProPlan] = useState(null);
  const [enterprisePlan, setEnterprisePlan] = useState(null);
  const [smallCredits, setSmallCredits] = useState(null);
  const [mediumCredits, setMediumCredits] = useState(null);
  const [largeCredits, setLargeCredits] = useState(null);

  useEffect(() => {
    if (open) loadData();
  }, [open]);

  const loadData = async () => {
    setLoading(true);
    try {
      const pkgData = await getPaymentPackages();
      const userData = await getCurrentUser();
      
      if (pkgData.subscriptions) {
        setFreePlan(pkgData.subscriptions.FREE);
        setStarterPlan(pkgData.subscriptions.STARTER);
        setProPlan(pkgData.subscriptions.PROFESSIONAL);
        setEnterprisePlan(pkgData.subscriptions.ENTERPRISE);
      }
      
      if (pkgData.packages) {
        setSmallCredits(pkgData.packages.small);
        setMediumCredits(pkgData.packages.medium);
        setLargeCredits(pkgData.packages.large);
      }
      
      setUserInfo(userData);
    } catch (err) {
      console.error('Error loading billing data:', err);
    }
    setLoading(false);
  };

  const buyPlan = async (tier) => {
    setProcessing(true);
    try {
      const result = await createSubscriptionCheckout(tier, 'monthly');
      window.location.href = result.checkout_url;
    } catch (err) {
      toast.error('Failed to start checkout');
      setProcessing(false);
    }
  };

  const buyCredits = async (pkgId) => {
    setProcessing(true);
    try {
      const result = await createCreditsCheckout(pkgId);
      window.location.href = result.checkout_url;
    } catch (err) {
      toast.error('Failed to start checkout');
      setProcessing(false);
    }
  };

  if (!open) return null;

  const currentTier = userInfo?.subscription_tier || 'FREE';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="h-6 w-6" />
            Billing & Subscription
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="space-y-6">
            {userInfo && (
              <div className="bg-blue-50 rounded-lg p-4 flex justify-between items-center">
                <div>
                  <p className="text-sm text-slate-500">Current Plan</p>
                  <p className="text-xl font-bold">{currentTier}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Credits</p>
                  <p className="text-2xl font-bold text-blue-600">{userInfo.credits_remaining || 0}</p>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant={tab === 'plans' ? 'default' : 'outline'} onClick={() => setTab('plans')}>
                <Crown className="h-4 w-4 mr-2" />Plans
              </Button>
              <Button variant={tab === 'credits' ? 'default' : 'outline'} onClick={() => setTab('credits')}>
                <Zap className="h-4 w-4 mr-2" />Credits
              </Button>
            </div>

            {tab === 'plans' && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {freePlan && (
                  <PlanItem 
                    name="FREE" 
                    price={freePlan.price_monthly} 
                    features={freePlan.features || []}
                    isCurrent={currentTier === 'FREE'}
                    disabled={processing}
                    onSelect={() => buyPlan('FREE')}
                  />
                )}
                {starterPlan && (
                  <PlanItem 
                    name="STARTER" 
                    price={starterPlan.price_monthly} 
                    features={starterPlan.features || []}
                    isCurrent={currentTier === 'STARTER'}
                    disabled={processing}
                    onSelect={() => buyPlan('STARTER')}
                  />
                )}
                {proPlan && (
                  <PlanItem 
                    name="PROFESSIONAL" 
                    price={proPlan.price_monthly} 
                    features={proPlan.features || []}
                    isCurrent={currentTier === 'PROFESSIONAL'}
                    isPopular
                    disabled={processing}
                    onSelect={() => buyPlan('PROFESSIONAL')}
                  />
                )}
                {enterprisePlan && (
                  <PlanItem 
                    name="ENTERPRISE" 
                    price={enterprisePlan.price_monthly} 
                    features={enterprisePlan.features || []}
                    isCurrent={currentTier === 'ENTERPRISE'}
                    disabled={processing}
                    onSelect={() => buyPlan('ENTERPRISE')}
                  />
                )}
              </div>
            )}

            {tab === 'credits' && (
              <div className="grid grid-cols-3 gap-4">
                {smallCredits && (
                  <CreditsItem 
                    name={smallCredits.description}
                    price={smallCredits.price}
                    credits={smallCredits.credits}
                    disabled={processing}
                    onSelect={() => buyCredits('small')}
                  />
                )}
                {mediumCredits && (
                  <CreditsItem 
                    name={mediumCredits.description}
                    price={mediumCredits.price}
                    credits={mediumCredits.credits}
                    isBest
                    disabled={processing}
                    onSelect={() => buyCredits('medium')}
                  />
                )}
                {largeCredits && (
                  <CreditsItem 
                    name={largeCredits.description}
                    price={largeCredits.price}
                    credits={largeCredits.credits}
                    disabled={processing}
                    onSelect={() => buyCredits('large')}
                  />
                )}
              </div>
            )}

            {processing && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
                  <p className="font-medium">Redirecting to payment...</p>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default BillingModal;
